/**
 * Chat image uploads to Firebase Storage.
 *
 * Web port of flock-expo/src/hooks/useMediaUpload.ts: accepts File/Blob
 * directly (no URIs), converts HEIC via heic2any (lazy-loaded), reads image
 * dimensions with createImageBitmap. Storage path and metadata are identical
 * to mobile — they must match storage.rules:
 *   chat/{organizationId}/{groupId}/images/{firebaseUid}/{fileName}
 */
import { useCallback, useRef, useState } from 'react';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { auth, storage } from '@/config/firebase';
import { useAuth } from '@/context/AuthContext';
import type { UploadProgress, UploadResult } from '@flock-types/chat.types';

interface UseMediaUploadOptions {
  groupId: string;
  /** Maximum file size in bytes (default: 10MB, same as mobile). */
  maxFileSize?: number;
}

export interface WebUploadResult extends UploadResult {
  imageWidth?: number;
  imageHeight?: number;
}

interface UseMediaUploadReturn {
  uploadImage: (file: File) => Promise<WebUploadResult>;
  progress: UploadProgress | null;
  cancelUpload: () => void;
  isUploading: boolean;
}

const ALLOWED_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
]);

function isHeic(file: File): boolean {
  return (
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    /\.(heic|heif)$/i.test(file.name)
  );
}

async function convertHeicToJpeg(file: File): Promise<{ blob: Blob; converted: boolean }> {
  if (!isHeic(file)) return { blob: file, converted: false };
  try {
    const { default: heic2any } = await import('heic2any');
    const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    const blob = Array.isArray(result) ? result[0] : result;
    return { blob, converted: true };
  } catch (error) {
    console.error('[useMediaUpload] HEIC conversion failed:', error);
    return { blob: file, converted: false };
  }
}

async function readImageDimensions(blob: Blob): Promise<{ width?: number; height?: number }> {
  try {
    const bitmap = await createImageBitmap(blob);
    const dims = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return dims;
  } catch {
    return {};
  }
}

export function useMediaUpload(options: UseMediaUploadOptions): UseMediaUploadReturn {
  const { groupId, maxFileSize = 10 * 1024 * 1024 } = options;
  const { currentOrganization } = useAuth();

  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const uploadTaskRef = useRef<ReturnType<typeof uploadBytesResumable> | null>(null);

  const uploadImage = useCallback(
    async (file: File): Promise<WebUploadResult> => {
      const firebaseUid = auth.currentUser?.uid;
      if (!firebaseUid) throw new Error('User not authenticated');
      if (!currentOrganization?.id) throw new Error('No organization selected');
      if (!ALLOWED_TYPES.has(file.type) && !isHeic(file)) {
        throw new Error('Unsupported image type');
      }

      setProgress({ progress: 0, status: 'pending' });
      setIsUploading(true);

      try {
        const { blob, converted } = await convertHeicToJpeg(file);

        if (blob.size > maxFileSize) {
          throw new Error(`File size exceeds ${Math.round(maxFileSize / 1024 / 1024)}MB limit`);
        }

        const dims = await readImageDimensions(blob);

        const timestamp = Date.now();
        const mimeType = converted ? 'image/jpeg' : file.type || 'image/jpeg';
        const extension = converted ? 'jpeg' : mimeType.split('/')[1] || 'jpg';
        const finalFileName = file.name
          ? converted
            ? file.name.replace(/\.(heic|heif)$/i, '.jpeg')
            : file.name
          : `image_${timestamp}.${extension}`;

        const storagePath = `chat/${currentOrganization.id}/${groupId}/images/${firebaseUid}/${finalFileName}`;
        const storageRef = ref(storage, storagePath);

        const uploadTask = uploadBytesResumable(storageRef, blob, {
          contentType: mimeType,
          customMetadata: {
            organizationId: currentOrganization.id,
            groupId,
            uploadedBy: firebaseUid,
            originalFileName: finalFileName,
          },
        });
        uploadTaskRef.current = uploadTask;

        return await new Promise<WebUploadResult>((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progressPercent = Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              );
              setProgress({ progress: progressPercent, status: 'uploading' });
            },
            (error) => {
              console.error('[useMediaUpload] Upload error:', error);
              setProgress({ progress: 0, status: 'error', error: error.message });
              setIsUploading(false);
              uploadTaskRef.current = null;
              reject(error);
            },
            async () => {
              try {
                const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
                setProgress({ progress: 100, status: 'complete' });
                setIsUploading(false);
                uploadTaskRef.current = null;
                resolve({
                  url: downloadUrl,
                  mimeType,
                  fileSize: blob.size,
                  fileName: finalFileName,
                  imageWidth: dims.width,
                  imageHeight: dims.height,
                });
              } catch (error) {
                console.error('[useMediaUpload] Failed to get download URL:', error);
                setProgress({ progress: 0, status: 'error', error: 'Failed to get download URL' });
                setIsUploading(false);
                uploadTaskRef.current = null;
                reject(error);
              }
            }
          );
        });
      } catch (error) {
        setProgress({
          progress: 0,
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
        });
        setIsUploading(false);
        throw error;
      }
    },
    [groupId, currentOrganization?.id, maxFileSize]
  );

  const cancelUpload = useCallback(() => {
    if (uploadTaskRef.current) {
      uploadTaskRef.current.cancel();
      uploadTaskRef.current = null;
      setProgress(null);
      setIsUploading(false);
    }
  }, []);

  return { uploadImage, progress, cancelUpload, isUploading };
}

export default useMediaUpload;
